import { cn, IconButton } from '@bridebook/aura';
import { ChevronLeft, ChevronRight } from '@bridebook/aura/src/icons';
import { ImageX } from '@bridebook/ui';
import { useState } from 'react';
import { CarouselDots } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/components/carousel-dots';
import { AUTOPLAY_INTERVAL } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/constants';
import { useEmblaWithPlugins } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/hooks/use-embla-with-plugins';

const arrowButtonStyles = cn(
  'absolute top-1/2 z-30 hidden -translate-y-1/2 shadow-md',
  'border-transparent! bg-space-75! text-white! hover:bg-space-60!',
  'opacity-0 transition-all duration-300 ease-out',
  'group-hover/carousel:translate-x-0 group-hover/carousel:opacity-100',
  'sm:flex',
);

type Props = {
  images: string[];
  imageWidth: number;
  imageHeight: number;
  autoplay?: boolean;
  autoplayInterval?: number;
};

export const VenueCarousel = ({
  images,
  imageWidth,
  imageHeight,
  autoplay = true,
  autoplayInterval = AUTOPLAY_INTERVAL.venue,
}: Props) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const { emblaRef, scrollPrev, scrollNext, scrollTo } = useEmblaWithPlugins({
    loop: true,
    autoplay,
    autoplayDelay: autoplayInterval,
    fade: false,
    onSelect: setActiveIndex,
    enabled: images.length > 1,
  });

  if (images.length === 0) return null;

  const showControls = images.length > 1;

  return (
    <div
      className="group/carousel relative w-full overflow-hidden"
      style={{ aspectRatio: `${imageWidth} / ${imageHeight}` }}>
      {/* Skeleton background - visible while image loads */}
      <div className="bg-space-10 absolute inset-0 animate-pulse" />

      <div ref={emblaRef} className="relative h-full w-full overflow-hidden">
        <div className="flex h-full">
          {images.map((src, index) => (
            <div key={src} className="h-full flex-[0_0_100%]">
              <ImageX
                src={src}
                w={imageWidth}
                h={imageHeight}
                fit="crop"
                loading={index <= 1 ? 'eager' : 'lazy'}
                classNames={{
                  wrapper: 'h-full w-full',
                  img: 'h-full w-full object-cover',
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {showControls && (
        <>
          <IconButton
            icon={ChevronLeft}
            onClick={scrollPrev}
            aria-label="Previous photo"
            className={cn(arrowButtonStyles, 'left-2.5 -translate-x-3')}
          />
          <IconButton
            icon={ChevronRight}
            onClick={scrollNext}
            aria-label="Next photo"
            className={cn(arrowButtonStyles, 'right-2.5 translate-x-3')}
          />
          <CarouselDots
            count={images.length}
            activeIndex={activeIndex}
            onDotClick={scrollTo}
            className="bottom-2 z-10 gap-2 rounded-full bg-black/30 px-2 py-1"
            classNames={{
              dot: 'data-[state=active]:bg-white data-[state=inactive]:bg-white/50 data-[state=inactive]:hover:bg-white/70',
            }}
            ariaLabel="Venue photo navigation"
          />
        </>
      )}
    </div>
  );
};
